import type * as z from 'zod/v4-mini';
import { useSessionStorage } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { readonly } from 'vue';
import { useAuth } from '#layers/access/stores/auth';
import { createZodSerializer } from '#layers/base/public';

export function readUserSessionRecord<Schema extends z.ZodMiniType, Fallback = z.infer<Schema>>(
	key: string,
	schema: Schema,
	fallback: Fallback,
): z.infer<Schema> | Fallback {
	if (typeof sessionStorage === 'undefined') return fallback;

	const raw = sessionStorage.getItem(key);

	return raw === null ? fallback : createZodSerializer(schema, fallback).read(raw);
}

export function useUserSessionRecord<Schema extends z.ZodMiniType, Fallback = z.infer<Schema>>(
	keyForUser: (userId: string) => string,
	schema: Schema,
	fallback: Fallback,
) {
	const { currentUser } = storeToRefs(useAuth());
	const serializer = createZodSerializer(schema, fallback);

	const state = useSessionStorage<z.infer<Schema> | Fallback>(
		() => keyForUser(currentUser.value?.id ?? ''),
		fallback,
		{ serializer },
	);

	function set(next: z.infer<Schema> | Fallback) {
		state.value = next;
	}

	function update(recipe: (current: z.infer<Schema> | Fallback) => z.infer<Schema> | Fallback) {
		const next = recipe(state.value);
		if (next === state.value) return;

		state.value = next;
	}

	return { state: readonly(state), set, update };
}
